import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  Fade,
  Slide,
  Zoom,
  Paper
} from "@mui/material";
import {
  Rocket as RocketIcon,
  AutoAwesome as MagicIcon,
  Diamond as DiamondIcon,
  TrendingUp as TrendingUpIcon,
  Share as ShareIcon,
  ArrowForward as ArrowForwardIcon,
  Twitter as TwitterIcon,
  Telegram as TelegramIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { trackPageView, trackLandingPageEvents } from "../utils/analytics";

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentTagline, setCurrentTagline] = useState(0);
  const [copied, setCopied] = useState(false);

  const taglines = [
    "Powered by memes, pixel by pixel.",
    "Never die from lack of memes again.",
    "fr, the memes will save us."
  ];

  // Meme gallery data - using actual pixelated memes
  const memeGallery = [
    {
      id: 1,
      src: "/pixelatedMemes/elon.png",
      title: "Elon Musk",
      description: "The meme lord himself 🚀"
    },
    {
      id: 2,
      src: "/pixelatedMemes/doge.png",
      title: "Doge",
      description: "Much wow, very pixel! 🐕"
    },
    {
      id: 3,
      src: "/pixelatedMemes/pepe.png",
      title: "Pepe",
      description: "Rare pepe pixelated 🐸"
    },
    {
      id: 4,
      src: "/pixelatedMemes/wojak.png",
      title: "Wojak",
      description: "Feels pixelated man 😢"
    },
    {
      id: 5,
      src: "/pixelatedMemes/brett.png",
      title: "Brett",
      description: "Pixelated and based 💪"
    },
    {
      id: 6,
      src: "/pixelatedMemes/solana.png",
      title: "Solana",
      description: "Fast and pixelated ⚡"
    },
    {
      id: 7,
      src: "/pixelatedMemes/pumpfun.png",
      title: "Pump.fun",
      description: "Pumping pixels! 📈"
    },
    {
      id: 8,
      src: "/pixelatedMemes/bunk.png",
      title: "Bunk",
      description: "Bunked and pixelated 🏠"
    },
    {
      id: 9,
      src: "/pixelatedMemes/cah.png",
      title: "CAH",
      description: "Cards Against Humanity 🃏"
    },
    {
      id: 10,
      src: "/pixelatedMemes/wlfi.png",
      title: "WLFI",
      description: "We Love Fucking It! ❤️"
    },
    {
      id: 11,
      src: "/pixelatedMemes/joe.png",
      title: "Joe",
      description: "Pixelated Joe 🧑"
    },
    {
      id: 12,
      src: "/pixelatedMemes/Solstice.png",
      title: "Solstice",
      description: "Pixelated solstice 🌅"
    },
    {
      id: 13,
      src: "/pixelatedMemes/shiba.png",
      title: "Shiba",
      description: "Shiba in pixel form 🐕"
    },
    {
      id: 14,
      src: "/pixelatedMemes/gme.png",
      title: "GME",
      description: "GameStop pixelated 🎮"
    },
    {
      id: 15,
      src: "/pixelatedMemes/axie.png",
      title: "Axie",
      description: "Pixelated Axie Infinity 🎮"
    },
    {
      id: 16,
      src: "/pixelatedMemes/cz.png",
      title: "CZ",
      description: "Binance CZ pixelated 💼"
    },
    {
      id: 17,
      src: "/pixelatedMemes/pengu.png",
      title: "Pengu",
      description: "Cool penguin pixels 🐧"
    },
    {
      id: 18,
      src: "/pixelatedMemes/troll.png",
      title: "Troll",
      description: "Trolling in pixels 😈"
    },
    {
      id: 19,
      src: "/pixelatedMemes/becker.png",
      title: "Becker",
      description: "Becker in pixels 🤯"
    },
    {
      id: 20,
      src: "/pixelatedMemes/ellio.png",
      title: "Ellio",
      description: "Ellio in pixels 🤯"
    },
    {
      id: 21,
      src: "/pixelatedMemes/fbb4.png",
      title: "Fbb4",
      description: "Fbb4 in pixels 🤯"
    }
  ];

  // Rotate taglines
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTagline((prev) => (prev + 1) % taglines.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [taglines.length]);

  const handleGetStarted = () => {
    trackLandingPageEvents.startPixelating();
    navigate("/app");
  };

  const handleJoinCommunity = () => {
    trackLandingPageEvents.joinCommunity();
    window.open("https://t.me/memefr_community", "_blank");
  };

  const handleExploreTweet = () => {
    trackLandingPageEvents.exploreTweet();
  };

  const handleStartCreatingMemes = () => {
    trackLandingPageEvents.startCreatingMemes();
    navigate("/app");
  };

  // Track page view on component mount
  useEffect(() => {
    trackPageView("/");
  }, []);

  const features = [
    {
      icon: <MagicIcon sx={{ fontSize: 40, color: "#00ff00" }} />,
      title: "Instant Pixelation",
      description:
        "Transform any image into pixel art in seconds. Perfect for memes, avatars, and NFTs."
    },
    {
      icon: <DiamondIcon sx={{ fontSize: 40, color: "#00ff00" }} />,
      title: "Diamond Hands Quality",
      description:
        "High-quality pixel art that never fades. Built for the long term, just like your diamond hands."
    },
    {
      icon: <ShareIcon sx={{ fontSize: 40, color: "#00ff00" }} />,
      title: "Viral Ready",
      description:
        "Create instantly shareable content that spreads like wildfire across social media."
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 40, color: "#00ff00" }} />,
      title: "Web3 Native",
      description:
        "Perfect for NFT collections, profile pictures, and crypto community identity."
    }
  ];

  const stats = [
    { label: "Memes Generated", value: "1M+", color: "#00ff00" },
    { label: "Diamond Hands", value: "10K+", color: "#ff6b35" },
    { label: "Pixels Created", value: "100M+", color: "#00d4ff" },
    { label: "Community Size", value: "50K+", color: "#ff00ff" }
  ];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)",
        color: "#ffffff",
        overflow: "hidden",
        position: "relative"
      }}
    >
      {/* Animated background elements */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(0, 255, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 107, 53, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(0, 212, 255, 0.1) 0%, transparent 50%)
          `,
          animation: "float 6s ease-in-out infinite"
        }}
      />

      {/* Header */}
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 2 }}>
        <Header />

        {/* Hero Section */}
        <Fade in timeout={1000}>
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2.5rem", md: "4rem", lg: "5rem" },
                fontWeight: "bold",
                background: "linear-gradient(45deg, #00ff00, #00d4ff, #ff00ff)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                textShadow: "0 0 30px rgba(0, 255, 0, 0.5)"
              }}
            >
              MEME fr
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.5rem", md: "2.5rem" },
                color: "#00ff00",
                mb: 3,
                fontFamily: "monospace"
              }}
            >
              ($MFR)
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Fade in key={currentTagline} timeout={500}>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { xs: "1.2rem", md: "1.8rem" },
                    color: "#ffffff",
                    mb: 2,
                    fontStyle: "italic"
                  }}
                >
                  "{taglines[currentTagline]}"
                </Typography>
              </Fade>
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: "1rem", md: "1.3rem" },
                color: "#cccccc",
                maxWidth: "800px",
                mx: "auto",
                mb: 4,
                lineHeight: 1.6
              }}
            >
              The Meme Generator that's ending the meme drought, one pixel at a
              time.
              <br />
            </Typography>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap"
              }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                startIcon={<RocketIcon />}
                sx={{
                  background: "linear-gradient(45deg, #00ff00, #00cc00)",
                  color: "#000",
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    background: "linear-gradient(45deg, #00cc00, #00ff00)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 10px 20px rgba(0, 255, 0, 0.3)"
                  },
                  transition: "all 0.3s ease"
                }}
              >
                Start Pixelating
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<TelegramIcon />}
                onClick={handleJoinCommunity}
                sx={{
                  borderColor: "#00d4ff",
                  color: "#00d4ff",
                  px: 4,
                  py: 1.5,
                  fontSize: "1.1rem",
                  "&:hover": {
                    borderColor: "#00d4ff",
                    background: "rgba(0, 212, 255, 0.1)",
                    transform: "translateY(-2px)"
                  },
                  transition: "all 0.3s ease"
                }}
              >
                Join Community
              </Button>
            </Box>
          </Box>
        </Fade>

        {/* Story Section */}
        <Slide direction="up" in timeout={1500}>
          <Paper
            sx={{
              background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
              border: "2px solid #00ff00",
              borderRadius: 3,
              p: 4,
              mb: 6,
              position: "relative",
              overflow: "hidden"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background:
                  "linear-gradient(90deg, #00ff00, #00d4ff, #ff00ff, #00ff00)",
                animation: "shimmer 2s linear infinite"
              }}
            />

            <Typography
              variant="h4"
              sx={{
                color: "#00ff00",
                mb: 3,
                textAlign: "center",
                fontFamily: "monospace"
              }}
            >
              The Story
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "#ffffff",
                mb: 3
              }}
            >
              It all started with Elon Musk dropping a tweet:{" "}
              <strong style={{ color: "#00ff00" }}>"fr"</strong> with an image
              saying
              <em style={{ color: "#ff6b35" }}>
                {" "}
                "You have died from lack of memes"
              </em>{" "}
              — a bull and cart moving forward under the weight of a meme
              drought.
            </Typography>

            {/* Elon's Tweet Visual */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 4
              }}
            >
              <Box
                component="a"
                href="https://x.com/elonmusk/status/1964143448350740579"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  maxWidth: "600px",
                  width: "100%",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "2px solid #00ff00",
                  boxShadow: "0 10px 30px rgba(0, 255, 0, 0.3)",
                  transition: "all 0.3s ease",
                  textDecoration: "none",
                  display: "block",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: "0 15px 40px rgba(0, 255, 0, 0.4)",
                    borderColor: "#00d4ff"
                  }
                }}
              >
                <img
                  src="/elon-tweet.png"
                  alt="Elon Musk's famous 'fr' tweet with 'YOU HAVE DIED FROM LACK OF MEMES'"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block"
                  }}
                />
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "#ffffff",
                mb: 3
              }}
            >
              That post was more than just a joke.{" "}
              <strong style={{ color: "#00d4ff" }}>It was a prophecy.</strong>
              <br />
              We're living in an age where memes fuel culture, markets, and
              entire communities — yet we still face the greatest crisis of all:
              <strong style={{ color: "#ff00ff" }}> Not Enough Memes.</strong>
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "#ffffff",
                textAlign: "center",
                fontStyle: "italic",
                mb: 3
              }}
            >
              <strong style={{ color: "#00ff00" }}>
                MEME fr ($MFR) was born to fix that.
              </strong>
            </Typography>

            {/* Explore Tweet Button */}
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="outlined"
                startIcon={<TwitterIcon />}
                href="https://x.com/elonmusk/status/1964143448350740579"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleExploreTweet}
                sx={{
                  borderColor: "#00ff00",
                  color: "#00ff00",
                  px: 4,
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: "bold",
                  "&:hover": {
                    borderColor: "#00ff00",
                    background: "rgba(0, 255, 0, 0.1)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 5px 15px rgba(0, 255, 0, 0.3)"
                  },
                  transition: "all 0.3s ease"
                }}
              >
                Explore Elon's Tweet
              </Button>
            </Box>
          </Paper>
        </Slide>

        {/* Features Section */}
        <Typography
          variant="h3"
          sx={{
            color: "#00ff00",
            textAlign: "center",
            mb: 4,
            fontFamily: "monospace"
          }}
        >
          Why MEME fr?
        </Typography>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Zoom in timeout={1000 + index * 200}>
                <Card
                  sx={{
                    background:
                      "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
                    border: "2px solid #00ff00",
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-5px)",
                      boxShadow: "0 10px 30px rgba(0, 255, 0, 0.3)",
                      borderColor: "#00d4ff"
                    }
                  }}
                >
                  <CardContent sx={{ textAlign: "center", p: 3 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: "#00ff00",
                        mb: 2,
                        fontWeight: "bold"
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#cccccc",
                        lineHeight: 1.6
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>

        {/* Meme Gallery Section */}
        <Typography
          variant="h3"
          sx={{
            color: "#ff00ff",
            textAlign: "center",
            mb: 4,
            fontFamily: "monospace"
          }}
        >
          Community Creations
        </Typography>

        <Paper
          sx={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
            border: "2px solid #ff00ff",
            borderRadius: 3,
            p: 4,
            mb: 6,
            position: "relative",
            overflow: "hidden"
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background:
                "linear-gradient(90deg, #ff00ff, #00ff00, #00d4ff, #ff00ff)",
              animation: "shimmer 3s linear infinite"
            }}
          />

          <Typography
            variant="h5"
            sx={{
              color: "#ff00ff",
              textAlign: "center",
              mb: 3,
              fontFamily: "monospace"
            }}
          >
            🔥 Fresh from the Pixel Lab 🔥
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#cccccc",
              textAlign: "center",
              mb: 4,
              fontSize: "1.1rem"
            }}
          >
            Check out these legendary pixelated memes created by our community!
          </Typography>

          {/* CHAOTIC MEME COLLAGE */}
          <Box
            sx={{
              position: "relative",
              height: { xs: "500px", sm: "600px", md: "700px" },
              overflow: "hidden",
              background:
                "linear-gradient(45deg, #0a0a0a 0%, #1a0a1a 25%, #0a1a0a 50%, #1a1a0a 75%, #0a0a0a 100%)",
              borderRadius: 4,
              p: { xs: 0.5, sm: 0.8, md: 1 },
              border: "4px solid #ff00ff",
              boxShadow: "0 0 50px rgba(255, 0, 255, 0.3)",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                  radial-gradient(circle at 20% 20%, rgba(255, 0, 255, 0.1) 0%, transparent 50%),
                  radial-gradient(circle at 80% 80%, rgba(0, 255, 0, 0.1) 0%, transparent 50%),
                  radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.05) 0%, transparent 50%)
                `,
                zIndex: 0
              }
            }}
          >
            {/* DYNAMIC MEME GALLERY */}
            {memeGallery.map((meme, index) => {
              // Define positions and styles for each meme
              const getMemeStyle = (index) => {
                const positions = [
                  // Main center meme (index 0)
                  {
                    top: "15%",
                    left: "35%",
                    width: { xs: "120px", sm: "150px", md: "220px" },
                    height: { xs: "120px", sm: "150px", md: "220px" },
                    transform: "rotate(-12deg)",
                    zIndex: 8,
                    border: "4px solid #ff00ff",
                    borderRadius: "20px",
                    boxShadow:
                      "0 20px 40px rgba(255, 0, 255, 0.6), inset 0 0 20px rgba(255, 0, 255, 0.2)",
                    showTitle: true
                  },
                  // Top left (index 1)
                  {
                    top: "5%",
                    left: "8%",
                    width: { xs: "80px", sm: "100px", md: "140px" },
                    height: { xs: "80px", sm: "100px", md: "140px" },
                    transform: "rotate(25deg)",
                    zIndex: 6,
                    border: "3px solid #00ff00",
                    borderRadius: "50%",
                    boxShadow: "0 15px 30px rgba(0, 255, 0, 0.5)",
                    showTitle: false
                  },
                  // Top right (index 2)
                  {
                    top: "3%",
                    right: "12%",
                    width: { xs: "90px", sm: "110px", md: "160px" },
                    height: { xs: "90px", sm: "110px", md: "160px" },
                    transform: "rotate(-30deg)",
                    zIndex: 7,
                    border: "3px solid #00d4ff",
                    borderRadius: "15px",
                    boxShadow: "0 15px 30px rgba(0, 212, 255, 0.5)",
                    showTitle: false
                  },
                  // Left side (index 3)
                  {
                    top: "40%",
                    left: "2%",
                    width: { xs: "70px", sm: "85px", md: "110px" },
                    height: { xs: "70px", sm: "85px", md: "110px" },
                    transform: "rotate(60deg)",
                    zIndex: 5,
                    border: "3px solid #ff6b35",
                    borderRadius: "25px",
                    boxShadow: "0 15px 30px rgba(255, 107, 53, 0.5)",
                    showTitle: false
                  },
                  // Right side (index 4)
                  {
                    top: "45%",
                    right: "5%",
                    width: { xs: "80px", sm: "95px", md: "120px" },
                    height: { xs: "80px", sm: "95px", md: "120px" },
                    transform: "rotate(-45deg)",
                    zIndex: 4,
                    border: "3px solid #ff00ff",
                    borderRadius: "30px",
                    boxShadow: "0 15px 30px rgba(255, 0, 255, 0.5)",
                    showTitle: false
                  },
                  // Bottom left (index 5)
                  {
                    bottom: "20%",
                    left: "15%",
                    width: { xs: "90px", sm: "110px", md: "150px" },
                    height: { xs: "90px", sm: "110px", md: "150px" },
                    transform: "rotate(15deg)",
                    zIndex: 9,
                    border: "3px solid #00ff00",
                    borderRadius: "40px",
                    boxShadow: "0 15px 30px rgba(0, 255, 0, 0.5)",
                    showTitle: false
                  },
                  // Bottom right (index 6)
                  {
                    bottom: "15%",
                    right: "20%",
                    width: { xs: "70px", sm: "85px", md: "115px" },
                    height: { xs: "70px", sm: "85px", md: "115px" },
                    transform: "rotate(-20deg)",
                    zIndex: 8,
                    border: "3px solid #00d4ff",
                    borderRadius: "50%",
                    boxShadow: "0 15px 30px rgba(0, 212, 255, 0.5)",
                    showTitle: false
                  },
                  // Center left (index 7)
                  {
                    top: "60%",
                    left: "25%",
                    width: { xs: "60px", sm: "70px", md: "90px" },
                    height: { xs: "60px", sm: "70px", md: "90px" },
                    transform: "rotate(75deg)",
                    zIndex: 3,
                    border: "3px solid #ff6b35",
                    borderRadius: "20px",
                    boxShadow: "0 15px 30px rgba(255, 107, 53, 0.5)",
                    showTitle: false
                  },
                  // Center right (index 8)
                  {
                    top: "65%",
                    right: "30%",
                    width: { xs: "70px", sm: "80px", md: "100px" },
                    height: { xs: "70px", sm: "80px", md: "100px" },
                    transform: "rotate(-60deg)",
                    zIndex: 2,
                    border: "3px solid #ff00ff",
                    borderRadius: "35px",
                    boxShadow: "0 15px 30px rgba(255, 0, 255, 0.5)",
                    showTitle: false
                  },
                  // Floating top (index 9)
                  {
                    top: "25%",
                    left: "60%",
                    width: { xs: "50px", sm: "60px", md: "80px" },
                    height: { xs: "50px", sm: "60px", md: "80px" },
                    transform: "rotate(45deg)",
                    zIndex: 1,
                    border: "3px solid #00ff00",
                    borderRadius: "50%",
                    boxShadow: "0 15px 30px rgba(0, 255, 0, 0.5)",
                    showTitle: false
                  },
                  // Floating bottom (index 10)
                  {
                    bottom: "40%",
                    left: "70%",
                    width: { xs: "45px", sm: "55px", md: "70px" },
                    height: { xs: "45px", sm: "55px", md: "70px" },
                    transform: "rotate(-30deg)",
                    zIndex: 1,
                    border: "3px solid #00d4ff",
                    borderRadius: "50%",
                    boxShadow: "0 15px 30px rgba(0, 212, 255, 0.5)",
                    showTitle: false
                  },
                  // Tiny chaos (index 11)
                  {
                    top: "50%",
                    left: "10%",
                    width: { xs: "40px", sm: "45px", md: "60px" },
                    height: { xs: "40px", sm: "45px", md: "60px" },
                    transform: "rotate(120deg)",
                    zIndex: 6,
                    border: "3px solid #ff6b35",
                    borderRadius: "50%",
                    boxShadow: "0 15px 30px rgba(255, 107, 53, 0.5)",
                    showTitle: false
                  }
                ];

                // For additional memes beyond the predefined positions, create random positions
                if (index >= positions.length) {
                  const randomTop = Math.random() * 70 + 10; // 10% to 80%
                  const randomLeft = Math.random() * 80 + 10; // 10% to 90%
                  const randomSize = Math.random() * 40 + 30; // 30px to 70px
                  const randomRotation = (Math.random() - 0.5) * 180; // -90 to 90 degrees
                  const colors = ["#ff00ff", "#00ff00", "#00d4ff", "#ff6b35"];
                  const randomColor =
                    colors[Math.floor(Math.random() * colors.length)];

                  return {
                    top: `${randomTop}%`,
                    left: `${randomLeft}%`,
                    width: {
                      xs: `${randomSize * 0.7}px`,
                      sm: `${randomSize * 0.85}px`,
                      md: `${randomSize}px`
                    },
                    height: {
                      xs: `${randomSize * 0.7}px`,
                      sm: `${randomSize * 0.85}px`,
                      md: `${randomSize}px`
                    },
                    transform: `rotate(${randomRotation}deg)`,
                    zIndex: Math.floor(Math.random() * 5) + 1,
                    border: `3px solid ${randomColor}`,
                    borderRadius:
                      Math.random() > 0.5
                        ? "50%"
                        : `${Math.random() * 30 + 10}px`,
                    boxShadow: `0 15px 30px ${randomColor}50`,
                    showTitle: false
                  };
                }

                return positions[index];
              };

              const memeStyle = getMemeStyle(index);
              const isMainMeme = index === 0;

              return (
                <Box
                  key={meme.id}
                  sx={{
                    position: "absolute",
                    ...memeStyle,
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: `${memeStyle.transform} scale(${
                        isMainMeme ? "1.2" : "1.3"
                      })`,
                      zIndex: 20
                    }
                  }}
                  onClick={() =>
                    trackLandingPageEvents.featureHover(meme.title)
                  }
                >
                  <img
                    src={meme.src}
                    alt={meme.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      imageRendering: "pixelated",
                      border: memeStyle.border,
                      borderRadius: memeStyle.borderRadius,
                      boxShadow: memeStyle.boxShadow
                    }}
                  />
                  {memeStyle.showTitle && (
                    <Typography
                      variant="caption"
                      sx={{
                        position: "absolute",
                        bottom: "-30px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: "#ff00ff",
                        fontWeight: "900",
                        fontSize: "1rem",
                        textAlign: "center",
                        background: "linear-gradient(45deg, #000, #333)",
                        px: 2,
                        py: 1,
                        borderRadius: 3,
                        border: "2px solid #ff00ff",
                        textShadow: "0 0 10px #ff00ff"
                      }}
                    >
                      {meme.title}
                    </Typography>
                  )}
                </Box>
              );
            })}

            {/* CHAOTIC FLOATING PARTICLES */}
            <Box
              sx={{
                position: "absolute",
                top: "10%",
                right: "30%",
                width: "12px",
                height: "12px",
                background: "linear-gradient(45deg, #ff00ff, #00ff00)",
                borderRadius: "50%",
                animation: "chaosFloat 2s ease-in-out infinite",
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "25%",
                left: "40%",
                width: "8px",
                height: "8px",
                background: "linear-gradient(45deg, #00d4ff, #ff6b35)",
                borderRadius: "50%",
                animation: "chaosFloat 3s ease-in-out infinite reverse",
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "70%",
                right: "10%",
                width: "15px",
                height: "15px",
                background: "linear-gradient(45deg, #00ff00, #ff00ff)",
                borderRadius: "50%",
                animation: "chaosFloat 4s ease-in-out infinite",
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: "30%",
                left: "20%",
                width: "6px",
                height: "6px",
                background: "linear-gradient(45deg, #ff6b35, #00d4ff)",
                borderRadius: "50%",
                animation: "chaosFloat 2.5s ease-in-out infinite",
                zIndex: 1
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: "50%",
                right: "40%",
                width: "10px",
                height: "10px",
                background: "linear-gradient(45deg, #ff00ff, #ff6b35)",
                borderRadius: "50%",
                animation: "chaosFloat 3.5s ease-in-out infinite reverse",
                zIndex: 1
              }}
            />

            {/* MEME TEXT OVERLAYS */}
            <Typography
              variant="h4"
              sx={{
                position: "absolute",
                top: "5%",
                left: "50%",
                transform: "translateX(-50%) rotate(-5deg)",
                color: "#ff00ff",
                fontWeight: "900",
                fontSize: { xs: "1.2rem", sm: "1.5rem", md: "2rem" },
                textShadow: "0 0 20px #ff00ff, 0 0 40px #ff00ff",
                zIndex: 10,
                fontFamily: "monospace"
              }}
            >
              MEME Universe
            </Typography>

            <Typography
              variant="h6"
              sx={{
                position: "absolute",
                bottom: "5%",
                right: "10%",
                transform: "rotate(10deg)",
                color: "#00ff00",
                fontWeight: "bold",
                fontSize: { xs: "0.8rem", sm: "1rem", md: "1.2rem" },
                textShadow: "0 0 15px #00ff00",
                zIndex: 10,
                fontFamily: "monospace"
              }}
            >
              CHAOS MODE
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center", mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleGetStarted}
              sx={{
                background: "linear-gradient(45deg, #ff00ff, #00ff00)",
                color: "#000",
                px: 4,
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: "bold",
                "&:hover": {
                  background: "linear-gradient(45deg, #00ff00, #ff00ff)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 10px 20px rgba(255, 0, 255, 0.4)"
                },
                transition: "all 0.3s ease"
              }}
            >
              Create Your Own Meme
            </Button>
          </Box>
        </Paper>

        {/* Tokenomics Section */}
        <Typography
          variant="h3"
          sx={{
            color: "#ff6b35",
            textAlign: "center",
            mb: 4,
            fontFamily: "monospace"
          }}
        >
          Tokenomics
        </Typography>

        <Paper
          sx={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
            border: "2px solid #ff6b35",
            borderRadius: 3,
            p: 4,
            mb: 6
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff6b35",
                    mb: 1,
                    fontWeight: "bold",
                    fontSize: "0.9rem"
                  }}
                >
                  TOKEN
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#00ff00",
                    fontWeight: "bold",
                    fontFamily: "monospace"
                  }}
                >
                  $MFR
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff6b35",
                    mb: 1,
                    fontWeight: "bold",
                    fontSize: "0.9rem"
                  }}
                >
                  SUPPLY
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#00d4ff",
                    fontWeight: "bold",
                    fontFamily: "monospace"
                  }}
                >
                  1B
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff6b35",
                    mb: 1,
                    fontWeight: "bold",
                    fontSize: "0.9rem"
                  }}
                >
                  LIQUIDITY
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#ff00ff",
                    fontWeight: "bold",
                    fontFamily: "monospace"
                  }}
                >
                  BURNED
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff6b35",
                    mb: 1,
                    fontWeight: "bold",
                    fontSize: "0.9rem"
                  }}
                >
                  CHAIN
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    color: "#00ff00",
                    fontWeight: "bold",
                    fontFamily: "monospace"
                  }}
                >
                  SOLANA
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Token Address Section */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{
                color: "#ff6b35",
                mb: 2,
                fontWeight: "bold",
                fontSize: "1rem"
              }}
            >
              TOKEN ADDRESS
            </Typography>
            <Box
              onClick={() => {
                navigator.clipboard.writeText("DhG4ix7WSwyErcYn1SsxVeVMmUryAp5JmA4X7Spbpump");
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              sx={{
                background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
                border: "2px solid #00ff00",
                borderRadius: 2,
                p: 2,
                mb: 2,
                cursor: "pointer",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "#00d4ff",
                  background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a2a 100%)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 5px 15px rgba(0, 255, 0, 0.3)"
                }
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: "#00ff00",
                  fontFamily: "monospace",
                  fontSize: "0.9rem",
                  wordBreak: "break-all",
                  lineHeight: 1.4,
                  userSelect: "all"
                }}
              >
                DhG4ix7WSwyErcYn1SsxVeVMmUryAp5JmA4X7Spbpump
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#cccccc",
                fontStyle: "italic",
                fontSize: "0.8rem"
              }}
            >
              {copied ? "✅ Copied to clipboard!" : "Click the address above to copy $MFR token address"}
            </Typography>
          </Box>
        </Paper>

        {/* Stats Section */}
        <Paper
          sx={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
            border: "2px solid #00d4ff",
            borderRadius: 3,
            p: 4,
            mb: 6
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#00d4ff",
              textAlign: "center",
              mb: 4,
              fontFamily: "monospace"
            }}
          >
            Community Stats Goals
          </Typography>

          <Grid container spacing={3}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="h3"
                    sx={{
                      color: stat.color,
                      fontWeight: "bold",
                      mb: 1,
                      textShadow: `0 0 20px ${stat.color}50`
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#cccccc",
                      fontSize: "0.9rem"
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Mission Section */}
        <Paper
          sx={{
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
            border: "2px solid #ff00ff",
            borderRadius: 3,
            p: 4,
            mb: 6
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#ff00ff",
              textAlign: "center",
              mb: 4,
              fontFamily: "monospace"
            }}
          >
            Our Mission
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#00ff00",
                    mb: 2,
                    fontWeight: "bold"
                  }}
                >
                  🚫 End the meme drought
                </Typography>
                <Typography variant="body2" sx={{ color: "#cccccc" }}>
                  No one should ever "die from lack of memes."
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#00d4ff",
                    mb: 2,
                    fontWeight: "bold"
                  }}
                >
                  ⚡ Empower creators
                </Typography>
                <Typography variant="body2" sx={{ color: "#cccccc" }}>
                  Tools to pixelate, remix, and drop memes at the speed of
                  culture.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#ff6b35",
                    mb: 2,
                    fontWeight: "bold"
                  }}
                >
                  🌐 Expand utility
                </Typography>
                <Typography variant="body2" sx={{ color: "#cccccc" }}>
                  From avatars to NFT collections, meme wars to identity in
                  Web3.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* CTA Section */}
        <Box
          sx={{
            textAlign: "center",
            background: "linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)",
            border: "2px solid #00ff00",
            borderRadius: 3,
            p: 6,
            mb: 4
          }}
        >
          <Typography
            variant="h3"
            sx={{
              color: "#00ff00",
              mb: 3,
              fontFamily: "monospace"
            }}
          >
            Ready to Never Die from Lack of Memes?
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: "#ffffff",
              mb: 4,
              fontStyle: "italic"
            }}
          >
            Elon said it best: <strong style={{ color: "#00ff00" }}>fr.</strong>
            <br />
            And with $MFR, we're making sure the world never runs out of memes
            again.
          </Typography>

          <Button
            variant="contained"
            size="large"
            onClick={handleStartCreatingMemes}
            endIcon={<ArrowForwardIcon />}
            sx={{
              background: "linear-gradient(45deg, #00ff00, #00d4ff)",
              color: "#000",
              px: 6,
              py: 2,
              fontSize: "1.2rem",
              fontWeight: "bold",
              "&:hover": {
                background: "linear-gradient(45deg, #00d4ff, #00ff00)",
                transform: "translateY(-3px)",
                boxShadow: "0 15px 30px rgba(0, 255, 0, 0.4)"
              },
              transition: "all 0.3s ease"
            }}
          >
            Start Creating Memes Now
          </Button>
        </Box>

        {/* Footer */}
        <Footer />
      </Container>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes chaosFloat {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg) scale(1);
          }
          25% {
            transform: translateY(-15px) rotate(90deg) scale(1.2);
          }
          50% {
            transform: translateY(-30px) rotate(180deg) scale(0.8);
          }
          75% {
            transform: translateY(-10px) rotate(270deg) scale(1.1);
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
      `}</style>
    </Box>
  );
};

export default LandingPage;
